import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export function WorkspaceSwitcher() {
  const { activeTenantId, memberships, switchActiveTenant } = useAuth();
  const [activeName, setActiveName] = useState<string>("");

  // Load current workspace name (RLS allows fetching only active tenant)
  useEffect(() => {
    let isMounted = true;
    async function loadName() {
      if (!activeTenantId) {
        setActiveName("");
        return;
      }
      const { data } = await supabase
        .from("calie_tenants")
        .select("name")
        .eq("tenant_id", activeTenantId)
        .maybeSingle();
      if (!isMounted) return;
      setActiveName(data?.name || activeTenantId.slice(0, 6));
    }
    loadName();
    return () => {
      isMounted = false;
    };
  }, [activeTenantId]);

  const others = useMemo(
    () => memberships.filter((m) => m.tenant_id !== activeTenantId),
    [memberships, activeTenantId]
  );

  const label = activeName || (activeTenantId ? activeTenantId.slice(0, 6) : "Select workspace");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" aria-label="Switch workspace">
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {others.length === 0 ? (
          <DropdownMenuItem disabled>No other workspaces</DropdownMenuItem>
        ) : (
          others.map((m) => (
            <DropdownMenuItem key={m.tenant_id} onClick={() => switchActiveTenant(m.tenant_id)}>
              {m.tenant_id.slice(0, 6)} • {m.role}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
